import { lazy, Suspense, type ReactNode } from 'react';
import { cx } from '../lib/cx.ts';
import { useUiT } from '../i18n/index.tsx';
import type { IllustrationKind } from '../illustrations/illustrations.tsx';

// The drawings load on first use, so they stay out of an app's first paint; their box (size × ¾) is kept meanwhile.
const PresetArt = lazy(() =>
  import('../illustrations/illustrations.tsx').then(m => ({
    default: function PresetArt({ kind, size }: { kind: IllustrationKind; size: number }) {
      const Preset = m.ILLUSTRATIONS[kind];
      return <Preset size={size} />;
    },
  })),
);

export type EmptyStateKind = IllustrationKind;

export interface EmptyStateProps {
  /** Preset: picks the illustration and the default Turkish/English copy (`kind="no-friends"` → “Henüz arkadaşın yok”). */
  kind?: EmptyStateKind;
  /** Custom illustration (overrides the preset's); `null` hides it. */
  illustration?: ReactNode;
  /** Overrides the preset title. */
  title?: ReactNode;
  /** Overrides the preset description; `null` hides it. */
  description?: ReactNode;
  /** A Button or two (“Arkadaş ekle”). */
  action?: ReactNode;
  /** Illustration width in px. Default 160. */
  size?: number;
  /** Heading level of the title. Default 2. */
  headingLevel?: 2 | 3 | 4;
  className?: string;
}

/** Centered empty / error state with a MOODS illustration, a short title and a supporting line. */
export function EmptyState({
  kind,
  illustration,
  title,
  description,
  action,
  size = 160,
  headingLevel = 2,
  className,
}: EmptyStateProps) {
  const t = useUiT();
  const art =
    illustration !== undefined ? (
      illustration
    ) : kind ? (
      <Suspense fallback={<span className="md-illo" style={{ display: 'block', width: size, height: Math.round(size * 0.75) }} aria-hidden="true" />}>
        <PresetArt kind={kind} size={size} />
      </Suspense>
    ) : null;
  const heading = title ?? (kind ? t(`empty.${kind}.title`) : null);
  const desc = description !== undefined ? description : kind ? t(`empty.${kind}.description`) : null;
  const H = `h${headingLevel}` as const;
  return (
    <div className={cx('md-empty', className)}>
      {art ? <div className="md-empty-art">{art}</div> : null}
      {heading ? <H className="md-empty-title">{heading}</H> : null}
      {desc ? <p className="md-empty-desc">{desc}</p> : null}
      {action ? <div className="md-empty-action">{action}</div> : null}
    </div>
  );
}
